import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { AssetVariantDTO } from "../../dto/AssetDTO";
import { AssetService } from "../../services/AssetService";

/**
 * Play one language version of an audio asset at a time, so every row in the list can be listened to:
 * without it, all audio rows look the same. The cached asset only holds thumbnail URIs, so the first
 * play fetches the content URIs of all its variants once.
 */
export const useVariantPlayer = (assetId: string | undefined) => {
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const audio = useRef<HTMLAudioElement>();
  const contentUris = useRef(new Map<string, string>());
  const [playingId, setPlayingId] = useState<string>();

  useEffect(() => () => audio.current?.pause(), []);
  useEffect(() => {
    // Another asset: its URIs and whatever was playing no longer apply.
    contentUris.current.clear();
    audio.current?.pause();
    setPlayingId(undefined);
  }, [assetId]);

  const toggle = useCallback(
    async (variant: AssetVariantDTO) => {
      audio.current?.pause();
      if (playingId === variant.id) {
        setPlayingId(undefined);
        return;
      }
      if (!contentUris.current.has(variant.id) && orgId && assetId) {
        const content = await AssetService.GetAssetByGuid(orgId, token, assetId, true, "large");
        content?.variants.forEach((v) => contentUris.current.set(v.id, v.downloadUri));
      }
      const uri = contentUris.current.get(variant.id);
      if (!uri) return;

      const element = new Audio(uri);
      element.onended = () => setPlayingId(undefined);
      audio.current = element;
      setPlayingId(variant.id);
      element.play().catch(() => setPlayingId(undefined));
    },
    [playingId, orgId, token, assetId]
  );

  return { playingId, toggle } as const;
};
